import React, {
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import { DropzoneInputProps, FileError, useDropzone } from 'react-dropzone';
import { Button, Input } from 'ncoded-component-library';
import UploadIcon from '../../icons/Upload.icon';
import { useTranslation } from 'react-i18next';
import { DropzoneFile, getProperLabel } from './utils';
import useDropzoneField from 'hooks/useDropzoneField';
import DropzoneRequestsContext from 'providers/DropzoneRequests/DropzoneRequests.context';
import DisplayDropzoneFile from './components/DisplayDropzoneFile';
import FileUploader from 'components/FileUploader';
import { DropzoneDelete, DropzoneUpload } from 'models/Dropzone';
import { XOR } from 'types';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { SUCCESS_MESSAGES } from 'request-responses';
import { isNull, sentryError } from 'utils';
import { SINGLE_FILE_MAX_SIZE, ONE_MB, ONE_GB } from 'data/global';

import './Dropzone.styles.scss';
import './Dropzone.styles.responsive.scss';

export const singleFileValidator =
  (fileSize: number = SINGLE_FILE_MAX_SIZE) =>
  (file: File) => {
    const sizeLabel =
      fileSize >= 1024 * ONE_MB
        ? `${fileSize / ONE_GB}GB`
        : `${fileSize / ONE_MB}MB`;

    if (file.size > fileSize) {
      return {
        code: 'name-too-large',
        message: `File too large. File limit is up to ${sizeLabel}.`,
      };
    }

    return null;
  };

type WithProvider = {
  withProvider: true;
};

type WithoutCustomName = {
  noCustomNames?: true;
};

type WithCustomName = {
  updateCustomFileName: (
    fileId: string,
    customFileName: string,
  ) => Promise<void>;
};

type WithProps = {
  files: DropzoneFile[];
  uploadRequest: DropzoneUpload;
  deleteRequest: DropzoneDelete;
};

export type DeleteMessage = {
  title: string;
  content: string;
};

export type CommonFileAction = {
  icon: ReactNode;
  action: (file: DropzoneFile) => void;
};

export type DropzoneProps = Omit<DropzoneInputProps, 'accept' | 'onChange'> & {
  className?: string;
  fileName: string;
  maxFiles?: number;
  deleteMessage?: DeleteMessage;
  showApplicationAsIframe?: boolean;
  accept?: string | string[];
  commonFileAction?: CommonFileAction;
  actionText?: string;
  dropMessage?: string;
  replaceFile?: boolean;
  sequentialUpload?: boolean;
  maxSize?: number;
  onFilesChange?: (filesArray: DropzoneFile[]) => void;
  onChange?: (filesArray: DropzoneFile[]) => void;
  onFileDelete?: (dropzoneFile: DropzoneFile, files: DropzoneFile[]) => void;
} & XOR<WithProps, WithProvider> &
  XOR<WithoutCustomName, WithCustomName>;

const Dropzone: React.FC<DropzoneProps> = (props) => {
  const { t } = useTranslation();
  const {
    files: cFiles = [],
    uploadRequest: cUploadRequest,
    deleteRequest: cDeleteRequest,
    updateFiles,
  } = useContext(DropzoneRequestsContext);
  const {
    files = cFiles,
    fileName,
    className,
    maxFiles,
    disabled,
    deleteMessage,
    accept,
    showApplicationAsIframe,
    commonFileAction,
    dropMessage,
    multiple,
    replaceFile = false,
    sequentialUpload = false,
    actionText,
    maxSize = SINGLE_FILE_MAX_SIZE,
    updateCustomFileName,
    onChange,
    onFileDelete,
    uploadRequest = cUploadRequest,
    deleteRequest = cDeleteRequest,
    ...rest
  } = props;
  const { popServerError, popSuccess } = useContext(PopNotificationsContext);

  const [nameToUpload, setNameToUpload] = useState('');
  const hasCustomNames = !!updateCustomFileName;

  const [uploadingFiles, onDropUpload, deltaValue] = useDropzoneField({
    fileName,
    files,
    maxFiles,
    onChange: onChange as (filesArray: DropzoneFile[] | DropzoneFile) => void,
    uploadRequest,
    updateFiles,
    nameToUpload,
    hasCustomNames,
    sequential: sequentialUpload,
  });

  const showDropzone = useMemo(
    () => !maxFiles || files.length + uploadingFiles.length < maxFiles,
    [files.length, maxFiles, uploadingFiles.length],
  );

  const handleFileDelete = useCallback(
    (dropzoneFile: DropzoneFile) => {
      (async () => {
        if (!deleteRequest)
          throw Error('Upload request is not passed as a prop');
        try {
          await deleteRequest(dropzoneFile, fileName);

          deltaValue.current = deltaValue.current.filter(
            (file) => file.key !== dropzoneFile.key,
          );

          onFileDelete?.(dropzoneFile, files);
          popSuccess(t(SUCCESS_MESSAGES.FILE_DELETED));
        } catch (e) {
          popServerError(e);
          sentryError(e);
        }
      })();
    },
    [
      deleteRequest,
      deltaValue,
      fileName,
      files,
      onFileDelete,
      popServerError,
      popSuccess,
      t,
    ],
  );

  const validateDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (
        files.some((existingFile: DropzoneFile) =>
          acceptedFiles.some(
            (uploadingFile) => uploadingFile.name === existingFile.name,
          ),
        )
      ) {
        return;
      } else {
        onDropUpload(acceptedFiles);
        hasCustomNames && setNameToUpload('');
      }
    },
    [files, hasCustomNames, onDropUpload],
  );

  const onDrop = useCallback(
    (acceptedFiles) => {
      validateDrop?.(acceptedFiles);
    },
    [validateDrop],
  );

  const isFileDuplicate = useCallback(
    (file: File) =>
      files.find((el) => file.name === el.name)
        ? {
            code: 'duplicated-file',
            message: t('duplicatedFilesError', { fileName: file.name }),
          }
        : null,
    [files, t],
  );

  const isMaxFiles = useCallback(
    (file: File) =>
      [...files, file].length > maxFiles && !replaceFile
        ? {
            code: 'max-files',
            message: t('maxFilesError', { maxFiles }),
          }
        : null,
    [files, maxFiles, replaceFile, t],
  );

  const composeValidators = useCallback(
    (
      file: File,
      validators: Array<(file: File) => FileError | null> = [
        isFileDuplicate,
        isMaxFiles,
        singleFileValidator(maxSize),
      ],
    ) =>
      validators.find((validator) => !isNull(validator(file)))?.(file) || null,
    [maxSize, isFileDuplicate, isMaxFiles],
  );

  const MAX_FILES = hasCustomNames ? 1 : maxFiles;

  const { getRootProps, getInputProps, isDragActive, fileRejections } =
    useDropzone({
      ...rest,
      disabled,
      accept,
      maxFiles: MAX_FILES,
      multiple: multiple || MAX_FILES > 1,
      onDrop,
      validator: composeValidators,
    });

  const { onClick, ...restOfRootProps } = getRootProps();

  const firstErrorMessage = useMemo(
    () => fileRejections[0]?.errors[0]?.message,
    [fileRejections],
  );

  const classes = classNames(
    'resolve-dropzone',
    {
      'resolve-dropzone--empty': !files.length && !uploadingFiles?.length,
      'resolve-dropzone--error': firstErrorMessage,
      'resolve-dropzone--has-custom-names': hasCustomNames,
      'resolve-dropzone--disabled': !files.length && disabled,
    },
    className,
  );

  const chooseFileClasses = classNames('choose-file', {
    'choose-file--hidden': isDragActive,
  });

  const dragItHereClasses = classNames({
    'drag-it-here--hidden': isDragActive,
  });

  const dropFileClasses = classNames('drop-file', {
    'drop-file--hidden': !isDragActive,
  });

  const RenderFiles = useMemo(
    () =>
      files.map((file) => (
        <DisplayDropzoneFile
          {...file}
          key={file._id}
          disabled={disabled}
          fileKey={file.key}
          deleteMessage={deleteMessage}
          hasCustomNames={hasCustomNames}
          showApplicationAsIframe={showApplicationAsIframe}
          commonFileAction={commonFileAction}
          replaceFile={replaceFile}
          onReplace={onClick}
          onFileDelete={handleFileDelete}
          updateCustomFileName={updateCustomFileName}
        />
      )),
    [
      commonFileAction,
      deleteMessage,
      disabled,
      files,
      handleFileDelete,
      hasCustomNames,
      onClick,
      replaceFile,
      showApplicationAsIframe,
      updateCustomFileName,
    ],
  );

  const RenderUploadingFiles = useMemo(
    () =>
      uploadingFiles.map((file) => <FileUploader key={file.name} {...file} />),
    [uploadingFiles],
  );

  return (
    <div className={classes} {...restOfRootProps}>
      <>
        {(!replaceFile || (!uploadingFiles.length && replaceFile)) &&
          RenderFiles}
        {RenderUploadingFiles}
      </>

      <input {...getInputProps()} />

      {showDropzone && (
        <>
          <div className="resolve-dropzone__dropzone-container">
            {hasCustomNames && (
              <Input
                className="resolve-dropzone__dropzone-container__input"
                placeholder={t('fileName')}
                value={nameToUpload}
                onChange={(ev) => {
                  setNameToUpload(ev.target.value);
                }}
              />
            )}
            <Button
              icon={UploadIcon}
              className={chooseFileClasses}
              onClick={onClick}
            >
              {actionText ||
                t(
                  accept && !Array.isArray(accept)
                    ? getProperLabel(accept)
                    : 'uploadFile',
                )}
            </Button>
            <p className={dropFileClasses}>{t('dropFile')}!</p>
            <p className={dragItHereClasses}>
              {dropMessage || t('orDragAndDrop')}
            </p>
            {firstErrorMessage && <small>{firstErrorMessage}</small>}
          </div>
        </>
      )}
    </div>
  );
};

export default Dropzone;
